import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Topbar from "../../../components/Topbar";

function Main({ 
  serviceList = [], 
  stepList = [], 
  selectedService,
  onServiceSelected,
  onPageInsert, 
  onRowSelected, 
  name 
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const fromService = location.state?.fromService;
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);

  const filteredSteps = useMemo(() => {
    if (!searchTerm.trim()) return stepList;
    const lowercased = searchTerm.trim().toLowerCase();
    return stepList.filter((step) => {
      if (!step) return false;
      return (
        (step.Step_Title || "").toLowerCase().includes(lowercased) ||
        String(step.Step_Order || "").includes(lowercased)
      );
    });
  }, [searchTerm, stepList]);

  const handleStepClick = (step, index) => {
    setSelectedRowIndex(index);
    if (typeof onRowSelected === "function") {
      onRowSelected(step);
    }
  };

  return (
    <>
      <Topbar
        breadcrumbs={
          fromService
            ? [
                { label: "ข้อมูลบริการ", link: "/service" },
                {
                  label: selectedService?.Service_Name || "บริการ",
                  link: "/service",
                  state: { editServiceId: selectedService?.Service_Id },
                },
                { label: name },
              ]
            : [{ label: name }]
        }
      />
      <div className="page-header d-flex justify-content-between align-items-center mb-3" style={{ marginTop: "20px" }}>
        <Breadcrumbs
          page={name}
          items={
            fromService
              ? [
                  { name: "ข้อมูลตั้งต้น", url: "/initialdata" },
                  { name: "ข้อมูลบริการ", url: "/service" },
                  { name: selectedService?.Service_Name || "บริการ", url: "/service" },
                  { name: name, url: "/servicestep" },
                ]
              : [
                  { name: "ข้อมูลตั้งต้น", url: "/initialdata" },
                  { name: "ข้อมูลบริการ", url: "/service" },
                  { name: name, url: "/servicestep" },
                ]
          }
        />
      </div>

      {/* Service Selection */}
      <div className="card shadow-sm mb-3 border-0" style={{ borderRadius: "12px" }}>
        <div className="card-body p-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="form-label fw-bold mb-0">เลือกบริการ</label>
            <button
              onClick={() => navigate("/service", { state: { create: true } })}
              className="btn d-flex align-items-center gap-2"
              style={{
                backgroundColor: "#E13F57",
                color: "#fff",
                borderRadius: "10px",
                padding: "10px 24px",
                fontWeight: "500",
                border: "none",
                boxShadow: "0 2px 8px rgba(225, 63, 87, 0.2)",
                fontSize: "14px",
              }}
              type="button"
            >
              <i className="fa fa-plus"></i>
              <span>สร้างข้อมูลบริการ</span>
            </button>
          </div>
          <select
            className="form-select"
            value={selectedService?.Service_Id || ""}
            onChange={(e) => {
              const service = serviceList.find(s => s.Service_Id === Number(e.target.value));
              if (service) onServiceSelected(service);
            }}
            style={{ borderRadius: "10px", fontSize: "14px" }}
          >
            <option value="">-- เลือกบริการ --</option>
            {serviceList.map((service) => (
              <option key={service.Service_Id} value={service.Service_Id}>
                {service.Service_Name}{service.Service_Code ? ` (${service.Service_Code})` : ""}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedService && (
        <>
          {/* Search and Action Bar */}
          <div className="card shadow-sm mb-3 border-0" style={{ borderRadius: "12px", overflow: "hidden" }}>
            <div className="card-body p-3">
              <div className="row g-3 align-items-center">
                <div className="col-md-6 col-lg-4">
                  <div className="position-relative">
                    <input
                      type="text"
                      placeholder="ค้นหาขั้นตอนการบริการ..."
                      className="form-control ps-5"
                      style={{
                        borderRadius: "10px",
                        border: "1px solid #e5e7eb",
                        padding: "10px 10px 10px 45px",
                        fontSize: "14px"
                      }}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <i
                      className="fas fa-search position-absolute"
                      style={{
                        left: "16px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#9ca3af"
                      }}
                    ></i>
                  </div>
                </div>
                <div className="col-md-6 col-lg-8 d-flex justify-content-end">
                  <button
                    onClick={onPageInsert}
                    className="btn text-white d-flex align-items-center gap-2"
                    style={{
                      backgroundColor: "#E13F57",
                      borderRadius: "10px",
                      padding: "10px 24px",
                      fontWeight: "500",
                      border: "none",
                      boxShadow: "0 2px 8px rgba(225, 63, 87, 0.2)",
                    }}
                    type="button"
                  >
                    <i className="fa fa-plus"></i>
                    <span>เพิ่มขั้นตอน</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Data Table Card */}
          <div className="card shadow-sm border-0" style={{ borderRadius: "12px", overflow: "hidden" }}>
            <div className="card-body p-0">
              <div className="table-responsive" style={{ maxHeight: "600px", overflowY: "auto" }}>
                <table className="table table-hover mb-0" style={{ fontSize: "14px", tableLayout: "fixed" }}>
                  <thead style={{
                    backgroundColor: "rgb(225,63,87)",
                    color: "#fff",
                    position: "sticky",
                    top: 0,
                    zIndex: 101,
                  }}>
                    <tr>
                      <th className="text-center py-3" style={{ width: "5%", fontWeight: "600", color: "#fff" }}>ลำดับ</th>
                      <th className="py-3" style={{ width: "20%", fontWeight: "600", color: "#fff" }}>ชื่อขั้นตอน</th>
                      <th className="py-3" style={{ width: "18%", fontWeight: "600", color: "#fff" }}>รายละเอียด</th>
                      <th className="py-3" style={{ width: "18%", fontWeight: "600", color: "#fff" }}>อุปกรณ์/สินค้า ที่ใช้</th>
                      <th className="text-center py-3" style={{ width: "8%", fontWeight: "600", color: "#fff" }}>ปริมาณ</th>
                      <th className="text-center py-3" style={{ width: "8%", fontWeight: "600", color: "#fff" }}>หน่วย</th>
                      <th className="text-center py-3" style={{ width: "9%", fontWeight: "600", color: "#fff" }}>ค่าเริ่มต้น</th>
                      <th className="text-center py-3" style={{ width: "9%", fontWeight: "600", color: "#fff" }}>สถานะ</th>
                      <th className="text-center py-3" style={{ width: "5%", fontWeight: "600", color: "#fff" }}>แก้ไข</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSteps.length > 0 ? (
                      filteredSteps.map((step, index) => (
                        <tr
                          key={step.Step_Id || index}
                          style={{
                            backgroundColor: index === selectedRowIndex ? "#fef2f2" : "",
                            cursor: "pointer",
                            borderBottom: "1px solid #f3f4f6"
                          }}
                          onClick={() => handleStepClick(step, index)}
                          onMouseEnter={(e) => {
                            if (index !== selectedRowIndex) {
                              e.currentTarget.style.backgroundColor = "#f9fafb";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (index !== selectedRowIndex) {
                              e.currentTarget.style.backgroundColor = "";
                            }
                          }}
                        >
                          <td className="text-center align-middle py-2">
                            {step.Step_Order || index + 1}
                          </td>
                          <td className="align-middle py-2" style={{ color: "#111827" }}>
                            {step.Step_Title || "-"}
                          </td>
                          <td className="align-middle py-2" style={{ color: "#111827" }}>
                            {step.Step_Description || '-'}
                          </td>
                          <td className="align-middle py-2" title={step.Item_Name || ""} style={{ color: "#111827", maxWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {step.Item_Name || "-"}
                          </td>
                          <td className="text-center align-middle py-2" style={{ color: "#111827" }}>
                            {step.Step_Qty != null && step.Step_Qty !== "" ? parseFloat(step.Step_Qty).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "-"}
                          </td>
                          <td className="text-center align-middle py-2" style={{ color: "#111827" }}>
                            {step.Step_Unit || "-"}
                          </td>
                          <td className="text-center align-middle py-2">
                            {step.IsDefault === 1 ? (
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: "#dbeafe",
                                  color: "#1e40af",
                                  padding: "6px 12px",
                                  borderRadius: "20px",
                                  fontWeight: "500",
                                  fontSize: "12px"
                                }}
                              >
                                <i className="fa fa-star me-1"></i>
                                ค่าเริ่มต้น
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td className="text-center align-middle py-2">
                            <span
                              className="badge"
                              style={{
                                backgroundColor: step.IsActive === 1 ? "#dcfce7" : "#fee2e2",
                                color: step.IsActive === 1 ? "#166534" : "#991b1b",
                                padding: "6px 12px",
                                borderRadius: "20px",
                                fontWeight: "500",
                                fontSize: "12px"
                              }}
                            >
                              {step.IsActive === 1 ? "ใช้งาน" : "ไม่ใช้งาน"}
                            </span>
                          </td>
                          <td className="text-center align-middle py-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStepClick(step, index);
                              }}
                              className="btn btn-sm"
                              style={{
                                backgroundColor: "#E13F57",
                                color: "#fff",
                                border: "none",
                                borderRadius: "6px",
                                padding: "4px 12px",
                              }}
                            >
                              <i className="fa fa-edit"></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center py-4 text-muted">
                          {searchTerm ? "ไม่พบข้อมูลที่ค้นหา" : "ยังไม่มีขั้นตอนการบริการ"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {!selectedService && (
        <div className="card shadow-sm border-0" style={{ borderRadius: "12px" }}>
          <div className="card-body text-center py-5">
            <i className="fas fa-info-circle text-muted" style={{ fontSize: "48px" }}></i>
            <p className="text-muted mt-3">กรุณาเลือกบริการเพื่อดูขั้นตอนการบริการ</p>
          </div>
        </div>
      )}
    </>
  );
}

export default Main;

